import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { apiService, SubscriptionPayload, User, Product } from '../services/api';
import ProductPreview from './ProductPreview';

interface SubscriptionCreateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const SubscriptionCreateModal: React.FC<SubscriptionCreateModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    userId: '',
    productId: '',
    currentPrice: '',
    isTrial: true,
    startedAt: '',
    endedAt: '',
    affiliateCode: '',
    promoCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [promoPreview, setPromoPreview] = useState<{
    discountAmount: number;
    totalAfterDiscount: number;
    message?: string;
  } | null>(null);

  useEffect(() => {
    if (visible) {
      fetchUsers();
      fetchProducts();
      // Set default startedAt to today
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, startedAt: today }));
    }
  }, [visible]);

  // Helper to get effective product price based on pricing history
  const getEffectiveProductPrice = (product: Product, targetDate?: Date): number => {
    const date = targetDate ? new Date(targetDate) : new Date();

    if (product.pricingHistory && product.pricingHistory.length > 0) {
      const applicableHistories = product.pricingHistory.filter((history) => {
        const start = new Date(history.startDate);
        const end = history.endDate ? new Date(history.endDate) : null;

        if (start > date) return false;
        if (end && end < date) return false;

        return true;
      });

      if (applicableHistories.length > 0) {
        applicableHistories.sort(
          (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
        );
        return applicableHistories[0].price;
      }
    }


    return product.basePrice;
  };

  // Track if user has manually edited the price
  const [isPriceManuallyEdited, setIsPriceManuallyEdited] = useState(false);

  useEffect(() => {
    // When product or start date is selected/changed, update currentPrice
    if (selectedProduct) {
      const targetDate = formData.startedAt ? new Date(formData.startedAt) : undefined;
      const effectivePrice = getEffectiveProductPrice(selectedProduct, targetDate);

      // Only auto-update if price hasn't been manually edited
      if (!isPriceManuallyEdited) {
        setFormData(prev => ({ ...prev, currentPrice: effectivePrice.toString() }));
      }
    }
  }, [selectedProduct, formData.startedAt, isPriceManuallyEdited]);

  // Recalculate promo preview whenever product, price, or promoCode changes
  useEffect(() => {
    const recalcPromo = async () => {
      if (!selectedProduct) {
        setPromoPreview(null);
        return;
      }
      const rawPromo = formData.promoCode.trim();
      if (!rawPromo) {
        setPromoPreview(null);
        return;
      }

      const targetDate = formData.startedAt ? new Date(formData.startedAt) : undefined;
      const basePrice =
        formData.currentPrice && !isNaN(parseFloat(formData.currentPrice))
          ? parseFloat(formData.currentPrice)
          : getEffectiveProductPrice(selectedProduct, targetDate);

      if (!basePrice || basePrice <= 0) {
        setPromoPreview(null);
        return;
      }

      try {
        const promoList = await apiService.getPromoCodes({});
        const now = new Date();
        const promo = promoList.promoCodes?.find((p) => {
          if (p.code !== rawPromo) return false;
          if (!p.isActive) return false;
          const start = new Date(p.startDate);
          if (start > now) return false;
          if (p.endDate) {
            const end = new Date(p.endDate);
            if (end < now) return false;
          }
          return p.products?.some((pp) => pp.productId === selectedProduct.id);
        });

        if (!promo) {
          setPromoPreview({
            discountAmount: 0,
            totalAfterDiscount: basePrice,
            message: 'Promo code is not valid for this product or period.',
          });
          return;
        }

        const minPurchase = promo.minPurchase ?? 0;
        if (basePrice < minPurchase) {
          setPromoPreview({
            discountAmount: 0,
            totalAfterDiscount: basePrice,
            message: `Minimum purchase for this promo is ${new Intl.NumberFormat('id-ID', {
              style: 'currency',
              currency: 'IDR',
              minimumFractionDigits: 0,
            }).format(minPurchase)}.`,
          });
          return;
        }

        const discountType = promo.discountType;
        const discountValue = promo.discountValue;
        const maxDiscount = promo.maxDiscount ?? null;

        let discountAmount = 0;
        if (discountType === 'percentage') {
          discountAmount = (basePrice * discountValue) / 100;
        } else {
          discountAmount = discountValue;
        }

        if (maxDiscount !== null && discountAmount > maxDiscount) {
          discountAmount = maxDiscount;
        }

        if (discountAmount > basePrice) {
          discountAmount = basePrice;
        }

        const totalAfterDiscount = basePrice - discountAmount;

        setPromoPreview({
          discountAmount,
          totalAfterDiscount,
        });
      } catch (error) {
        console.error('Failed to preview promo discount:', error);
        setPromoPreview(null);
      }
    };

    recalcPromo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.promoCode, formData.currentPrice, formData.startedAt, selectedProduct]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getUsers({ limit: 100 });
      setUsers(response.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await apiService.getProducts({ limit: 100, includeDeleted: 'false' });
      setProducts(response.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const checkExistingActiveSubscription = async (userId: number) => {
    try {
      const response = await apiService.getSubscriptions({
        userId,
        status: 'ACTIVE' as any,
        limit: 1,
      });
      if (response.subscriptions && response.subscriptions.length > 0) {
        // TODO: Display warning to user about existing active subscription
        console.log('User has existing active subscription:', response.subscriptions[0]);
      }
    } catch (error) {
      console.error('Failed to check existing active subscription:', error);
    }
  };

  const handleProductSelect = async (product: Product) => {
    // Fetch full product details with feature limits
    try {
      const productDetail = await apiService.getProduct(product.id);
      setSelectedProduct(productDetail);
      setProductSearchTerm(productDetail.name);
      setIsPriceManuallyEdited(false); // Reset flag when product changes
      setFormData(prev => ({
        ...prev,
        productId: productDetail.id.toString(),
        // currentPrice will be auto-updated by useEffect based on pricing history
      }));
      setShowProductDropdown(false);
    } catch (error) {
      console.error('Failed to fetch product details:', error);
      setSelectedProduct(product);
      setProductSearchTerm(product.name);
      setIsPriceManuallyEdited(false); // Reset flag when product changes
      setFormData(prev => ({
        ...prev,
        productId: product.id.toString(),
        // currentPrice will be auto-updated by useEffect based on pricing history
      }));
      setShowProductDropdown(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.userId || !formData.productId) {
      alert('Please select both user and product');
      return;
    }

    try {
      setLoading(true);

      const affiliateCode = formData.affiliateCode.trim();
      const promoCode = formData.promoCode.trim();

      const payload: SubscriptionPayload = {
        userId: parseInt(formData.userId),
        productId: parseInt(formData.productId),
        affiliateCode: !!affiliateCode ? affiliateCode : undefined,
        promoCode: !!promoCode ? promoCode : undefined,
        currentPrice: formData.currentPrice ? parseFloat(formData.currentPrice) : undefined,
        isTrial: formData.isTrial,
        startedAt: formData.startedAt || undefined,
        endedAt: formData.endedAt || undefined,
      };

      await apiService.createSubscription(payload);
      resetForm();
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create subscription:', error);
      alert(error.message || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      userId: '',
      productId: '',
      currentPrice: '',
      isTrial: true,
      startedAt: today,
      endedAt: '',
      affiliateCode: '',
      promoCode: '',
    });
    setSelectedProduct(null);
    setUserSearchTerm('');
    setProductSearchTerm('');
    setShowUserDropdown(false);
    setShowProductDropdown(false);
    setIsPriceManuallyEdited(false);
  };

  const filteredUsers = users.filter(user => {
    if (!userSearchTerm) return true;
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.name?.toLowerCase().includes(searchLower) ||
      user.username?.toLowerCase().includes(searchLower)
    );
  });

  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const searchLower = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(searchLower) ||
      product.slug?.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower)
    );
  });

  const selectedUser = users.find(u => u.id === parseInt(formData.userId));

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">Create Subscription</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Form */}
              <div className="space-y-4">
                {/* User Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search user by email or name..."
                        value={userSearchTerm}
                        onChange={(e) => {
                          setUserSearchTerm(e.target.value);
                          setShowUserDropdown(true);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        disabled={loadingUsers}
                      />
                    </div>
                    {showUserDropdown && (
                      <>
                        <div
                          className="fixed inset-0 z-20"
                          onClick={() => setShowUserDropdown(false)}
                        />
                        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {filteredUsers.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500">No users found</div>
                          ) : (
                            filteredUsers.map(user => (
                              <button
                                key={user.id}
                                type="button"
                                onClick={async () => {
                                  setFormData(prev => ({ ...prev, userId: user.id.toString() }));
                                  setUserSearchTerm(user.name || user.email || '');
                                  setShowUserDropdown(false);
                                  // Check for existing active subscription
                                  await checkExistingActiveSubscription(user.id);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-2"
                              >
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.name || 'No name'}
                                  </div>
                                  <div className="text-xs text-gray-500">{user.email}</div>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {selectedUser && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-md">
                      <div className="text-sm font-medium text-gray-900">
                        {selectedUser.name || 'No name'}
                      </div>
                      <div className="text-xs text-gray-500">{selectedUser.email}</div>
                    </div>
                  )}
                </div>

                {/* Product Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search product by name, slug, or description..."
                        value={productSearchTerm}
                        onChange={(e) => {
                          setProductSearchTerm(e.target.value);
                          setShowProductDropdown(true);
                          if (!e.target.value) {
                            setSelectedProduct(null);
                            setFormData(prev => ({ ...prev, productId: '' }));
                          }
                        }}
                        onFocus={() => setShowProductDropdown(true)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        disabled={loadingProducts}
                        required
                      />
                    </div>
                    {showProductDropdown && (
                      <>
                        <div
                          className="fixed inset-0 z-20"
                          onClick={() => setShowProductDropdown(false)}
                        />
                        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {filteredProducts.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500">No products found</div>
                          ) : (
                            filteredProducts.map(product => (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => handleProductSelect(product)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between"
                              >
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {product.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(product.basePrice)} • {product.slug}
                                  </div>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {selectedProduct && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-md">
                      <div className="text-sm font-medium text-gray-900">
                        {selectedProduct.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(
                          getEffectiveProductPrice(
                            selectedProduct,
                            formData.startedAt ? new Date(formData.startedAt) : undefined,
                          ),
                        )}{' '}
                        • {selectedProduct.billingCycle}
                      </div>
                    </div>
                  )}
                </div>

                {/* Affiliate Code (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Affiliate Code <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.affiliateCode}
                    onChange={(e) => setFormData((prev) => ({ ...prev, affiliateCode: e.target.value }))}
                    placeholder="Enter affiliate code (e.g. AFF123)"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If a valid affiliate code is provided and the selected product is in an active affiliate
                    program, a pending commission will be created automatically.
                  </p>
                </div>

                {/* Promo Code (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promo Code <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.promoCode}
                    onChange={(e) => setFormData((prev) => ({ ...prev, promoCode: e.target.value }))}
                    placeholder="Enter promo code (must be valid for selected product)"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The promo code must exist and be linked to the selected product.
                  </p>
                  {promoPreview && (
                    <div className="mt-2 text-xs text-gray-700 space-y-1">
                      <div>
                        Estimated Discount:{' '}
                        <span className="font-semibold">
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                            minimumFractionDigits: 0,
                          }).format(promoPreview.discountAmount)}
                        </span>
                      </div>
                      <div>
                        Estimated Total After Discount:{' '}
                        <span className="font-semibold">
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                            minimumFractionDigits: 0,
                          }).format(promoPreview.totalAfterDiscount)}
                        </span>
                      </div>
                      {promoPreview.message && (
                        <div className="text-[11px] text-yellow-700 bg-yellow-50 border border-yellow-100 rounded px-2 py-1">
                          {promoPreview.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Current Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Price (IDR)
                  </label>
                  <input
                    type="number"
                    value={formData.currentPrice}
                    onChange={(e) => {
                      setIsPriceManuallyEdited(true);
                      setFormData(prev => ({ ...prev, currentPrice: e.target.value }));
                    }}
                    min="0"
                    step="0.01"
                    placeholder="Leave empty to use effective price from pricing history"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If not specified, will use the product's effective price based on pricing history for the start date
                  </p>
                </div>

                {/* Trial Flag */}
                <div className="flex items-center space-x-2">
                  <input
                    id="isTrial"
                    type="checkbox"
                    checked={formData.isTrial}
                    onChange={(e) => setFormData(prev => ({ ...prev, isTrial: e.target.checked }))}
                    className="h-4 w-4 text-imola-primary border-gray-300 rounded"
                  />
                  <label htmlFor="isTrial" className="text-sm text-gray-700">
                    Trial subscription (no initial payment, status starts as INACTIVE)
                  </label>
                </div>

                {/* Started At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Started At
                  </label>
                  <input
                    type="date"
                    value={formData.startedAt}
                    onChange={(e) => setFormData(prev => ({ ...prev, startedAt: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If not specified, will use current date
                  </p>
                </div>

                {/* Ended At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ended At (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.endedAt}
                    onChange={(e) => setFormData(prev => ({ ...prev, endedAt: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If not specified, will calculate from product billing cycle
                  </p>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="lg:sticky lg:top-20">
                {selectedProduct ? (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Product Preview</h3>
                    <ProductPreview
                      product={selectedProduct}
                      currentPrice={formData.currentPrice ? parseFloat(formData.currentPrice) : undefined}
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <p className="text-sm text-gray-500">Select a product to see preview</p>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80 focus:outline-none focus:ring-2 focus:ring-imola-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Subscription'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCreateModal;
